import { useEffect, useState, useCallback } from "react";
import HeaderNav from "@/components/HeaderNav";
import ShiftDashboard from "@/components/ShiftDashboard";
import AdaptiveTimeline from "@/components/AdaptiveTimeline";
import ShiftCalendar from "@/components/ShiftCalendar";
import AiCircadianAdvisor from "@/components/AiCircadianAdvisor";
import SleepLog from "@/components/SleepLog";
import AlertsReminders from "@/components/AlertsReminders";
import ExerciseRecommendations from "@/components/ExerciseRecommendations";
import NutritionRecommendations from "@/components/NutritionRecommendations";
import BadgesShare from "@/components/BadgesShare";
import TeamLeaderboard from "@/components/TeamLeaderboard";
import AlertnessMonitor from "@/components/AlertnessMonitor";
import JetLagMode from "@/components/JetLagMode";
import ReviewSubmitDialog from "@/components/ReviewSubmitDialog";
import ReviewsSection from "@/components/ReviewsSection";
import OnboardingSplash from "@/components/OnboardingSplash";
import Paywall from "@/components/Paywall";
import { http } from "@/lib/api";

export default function Dashboard() {
  const initialView = (() => {
    if (typeof window === "undefined") return "dashboard";
    const p = new URLSearchParams(window.location.search).get("view");
    const allowed = ["dashboard","calendar","timeline","log","badges","team","alertness","jetlag","reviews","exercise","nutrition","ai","alerts"];
    return allowed.includes(p) ? p : "dashboard";
  })();
  const [view, setView] = useState(initialView);
  const [shifts, setShifts] = useState([]);
  const [plan, setPlan] = useState(null);
  const [stats, setStats] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const refreshShifts = useCallback(async () => {
    const [s, p, st] = await Promise.all([
      http.get("/shifts").then((r) => r.data).catch(() => []),
      http.get("/sleep-plan?hours=72").then((r) => r.data).catch(() => null),
      http.get("/sleep-stats").then((r) => r.data).catch(() => null),
    ]);
    setShifts(s);
    setPlan(p);
    setStats(st);
  }, []);

  useEffect(() => { refreshShifts(); }, [refreshShifts]);

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-100 grain relative">
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
      <HeaderNav view={view} onView={setView} />

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        {view === "dashboard" && (
          <div className="space-y-6" data-testid="patient-dashboard">
            <ShiftDashboard plan={plan} stats={stats} onGoto={setView} />
            <AdaptiveTimeline plan={plan} />
          </div>
        )}
        {view === "calendar" && <ShiftCalendar shifts={shifts} onChange={refreshShifts} />}
        {view === "timeline" && <AdaptiveTimeline plan={plan} standalone />}
        {view === "log" && <SleepLog onGoToBadges={() => setView("badges")} onInviteReview={() => setReviewOpen(true)} />}
        {view === "badges" && <BadgesShare />}
        {view === "team" && (
          <Paywall feature="Team Leaderboard" description="Rally your rota. Weekly ranking, persona filters, and podium — for Pro members.">
            <TeamLeaderboard />
          </Paywall>
        )}
        {view === "alertness" && (
          <Paywall feature="Safety Alertness Score" description="Fatigue-risk prediction for every upcoming shift. Critical for pilots, train drivers, and healthcare.">
            <AlertnessMonitor />
          </Paywall>
        )}
        {view === "jetlag" && (
          <Paywall feature="Jet Lag Mode" description="Multi-leg travel planner with pre-adapt sleep windows, in-flight naps, and destination light exposure.">
            <JetLagMode />
          </Paywall>
        )}
        {view === "reviews" && <ReviewsSection />}
        {view === "exercise" && <ExerciseRecommendations />}
        {view === "nutrition" && <NutritionRecommendations />}
        {view === "ai" && <AiCircadianAdvisor />}
        {view === "alerts" && <AlertsReminders />}
      </main>

      <ReviewSubmitDialog open={reviewOpen} onOpenChange={setReviewOpen} />
      <OnboardingSplash />
    </div>
  );
}
